import { IS_LOCAL_SWAGMAGIC_BUILD, SWAGMAGIC_DOMAIN, isDev } from "@constants/env-variables";
import { INVITE_TOKEN_UUID_SEPERATOR } from "@constants/routes";
import {
  desktopScreenType,
  mobileScreenType,
  MY_WORKSPACE_SLUG,
  tabletScreenType,
} from "@stadium/constants";

export interface DecodedMeta {
  /** Root meta value (e.g. hostname like "localhost") */
  hostname: string;
  isSwagmagic: boolean;
  /** Device type when present (e.g. "mobile", "desktop", "tablet") */
  deviceType?: typeof mobileScreenType | typeof tabletScreenType | typeof desktopScreenType;
  /** Store number when present (e.g. "S1234") */
  storeNumber?: string;
  /** Store slug when present (e.g. "store-slug") */
  storeSlug?: string;
  origin: string;
  rootPath?: string;
}

export function encodeMeta(
  meta: string,
  deviceType: string,
  storeNumber: string,
  storeSlug: string,
  rootPath: string,
): string {
  return [meta, deviceType, storeNumber, storeSlug, rootPath].join("_");
}

export function decodeMeta(encoded: string): DecodedMeta {
  if (!encoded?.trim()) {
    return { hostname: "", isSwagmagic: false, origin: "" };
  }

  const parts = encoded.trim().split("_");
  const [host, deviceType, storeNumber, storeSlug, rootPath] = parts;

  return {
    hostname: host,
    isSwagmagic: host === SWAGMAGIC_DOMAIN || IS_LOCAL_SWAGMAGIC_BUILD,
    deviceType: deviceType as
      | typeof mobileScreenType
      | typeof tabletScreenType
      | typeof desktopScreenType,
    storeNumber,
    storeSlug,
    origin: isDev() ? `http://${host}:3000` : `https://${host}`,
    rootPath,
  };
}

export const getRedirectPathnameForCart = ({
  pathname,
  workspaceSlugFromUuid,
  options,
}: {
  pathname: string;
  workspaceSlugFromUuid: string;
  options: {
    isNewStoreRoutePersonalSpaceCartPage: boolean;
  };
}): string | undefined => {
  const pathSegments = pathname.split("/");

  const { isNewStoreRoutePersonalSpaceCartPage } = options;

  if (isNewStoreRoutePersonalSpaceCartPage) {
    // /[locale]/s/[storeIdentifier]/cart
    const [, localeCode, , storeIdentifier] = pathSegments;

    if (workspaceSlugFromUuid !== MY_WORKSPACE_SLUG) {
      return `/${localeCode}/w/${workspaceSlugFromUuid}/${storeIdentifier}/cart`;
    }

    return undefined;
  }

  // Legacy: /[country]/[lang]/stores/[slug]/[storeNumber]/cart
  const [, legacyCountryCode, legacyLang, , legacySlug, legacyStoreNumber] = pathSegments;
  const hasWorkspaceInLegacySlug = legacySlug?.includes(INVITE_TOKEN_UUID_SEPERATOR);
  if (hasWorkspaceInLegacySlug) {
    return undefined;
  }
  return `/${legacyCountryCode}/${legacyLang}/stores/${legacySlug}${INVITE_TOKEN_UUID_SEPERATOR}${workspaceSlugFromUuid}/${legacyStoreNumber}/cart`;
};
